import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Global hook that listens for new incoming chat messages
 * and shows a toast notification regardless of current page.
 */
export const useMessageNotifications = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const profileCacheRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('global-message-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
        },
        async (payload) => {
          const msg = payload.new as any;

          // Only notify if the message is for us and not sent by us
          if (msg.recipient_id !== user.id) return;

          // Resolve sender name
          let senderName = profileCacheRef.current.get(msg.sender_id);
          if (!senderName) {
            const { data: profile } = await supabase
              .from('profiles_public')
              .select('company_name, user_type')
              .eq('id', msg.sender_id)
              .single();

            if (profile) {
              senderName = profile.company_name || '';
              profileCacheRef.current.set(msg.sender_id, senderName);
            }
          }

          const preview =
            msg.message.length > 60
              ? msg.message.slice(0, 60) + '…'
              : msg.message;

          toast(senderName || 'Uusi viesti', {
            description: preview,
            action: {
              label: '→',
              onClick: () => navigate('/messages'),
            },
            duration: 5000,
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, navigate]);
};
